import { type ReactNode } from "react";
import { type LucideIcon } from "lucide-react";

import Breadcrumbs, { type BreadcrumbItemData } from "@/components/layout/Breadcrumbs";
import { metaChipClass, slateCardClass, slateSurfaceClass } from "@/lib/card-styles";
import { typeBodyClass, typeDisplayClass, typeMetaClass, typeNumericClass } from "@/lib/typography";
import { cn } from "@/lib/utils";

interface PageHeroProps {
    title: string;
    description: string;
    eyebrow?: string;
    icon?: LucideIcon;
    iconClassName?: string;
    breadcrumbs?: BreadcrumbItemData[];
    showHome?: boolean;
    badges?: ReactNode;
    actions?: ReactNode;
    meta?: ReactNode;
    className?: string;
}

const PageHero = ({
    title,
    description,
    eyebrow,
    icon: Icon,
    iconClassName = "text-primary",
    breadcrumbs,
    showHome,
    badges,
    actions,
    meta,
    className,
}: PageHeroProps) => {
    return (
        <div className={className}>
            {breadcrumbs && (
                <div className="mb-3">
                    <Breadcrumbs items={breadcrumbs} showHome={showHome} />
                </div>
            )}

            <div
                data-tour="page-hero"
                className={cn(
                    "relative overflow-hidden",
                    slateCardClass,
                    "p-5 sm:p-6 lg:p-8",
                    // `dark:from-stone-900/40` on a `stone-950` page measured 1.05:1 — the task
                    // header had no surface at all at night. One step up, and a warmer
                    // corner, gives it the same "sheet on a desk" read as the cards.
                    "bg-gradient-to-br from-stone-50 via-background to-amber-50/30 dark:from-stone-800/50 dark:via-stone-900 dark:to-amber-950/25"
                )}
            >
                <div
                    className="pointer-events-none absolute inset-y-0 right-0 w-1/3 opacity-[0.35] dark:opacity-20"
                    aria-hidden="true"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 1px 1px, hsl(var(--muted-foreground) / 0.18) 1px, transparent 0)",
                        backgroundSize: "14px 14px",
                        maskImage: "linear-gradient(to right, transparent, black 50%)",
                        WebkitMaskImage: "linear-gradient(to right, transparent, black 50%)",
                    }}
                />

                <div className="relative flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex-1 min-w-0 space-y-4">
                        {eyebrow && Icon && (
                            <span className={cn(metaChipClass, "uppercase tracking-wider")}>
                                <Icon className={cn("h-3.5 w-3.5", iconClassName)} aria-hidden="true" />
                                {eyebrow}
                            </span>
                        )}

                        <div>
                            <h1 className={typeDisplayClass}>{title}</h1>
                            <p className={cn(typeBodyClass, "mt-2 max-w-2xl text-balance sm:text-base")}>
                                {description}
                            </p>
                        </div>

                        {badges && <div className="flex flex-wrap gap-2">{badges}</div>}

                        {actions && <div className="flex flex-wrap items-center gap-2 pt-1">{actions}</div>}
                    </div>

                    {meta && (
                        <div className="flex flex-col gap-3 shrink-0 lg:items-end">{meta}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

interface HeroMetaCardProps {
    label: string;
    value: ReactNode;
    icon: LucideIcon;
    iconClassName?: string;
    className?: string;
}

const HeroMetaCard = ({
    label,
    value,
    icon: Icon,
    iconClassName = "bg-primary/10 text-primary",
    className,
}: HeroMetaCardProps) => (
    <div
        className={cn(
            slateSurfaceClass,
            "flex items-center gap-3 !py-2.5 px-3.5",
            className
        )}
    >
        <span
            className={cn(
                "inline-flex h-9 w-9 items-center justify-center rounded-lg shrink-0",
                iconClassName
            )}
        >
            <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
            <p className={cn(typeMetaClass, "leading-tight")}>{label}</p>
            <div className={cn(typeNumericClass, "text-sm font-bold leading-tight")}>{value}</div>
        </div>
    </div>
);

export { PageHero, HeroMetaCard };
export type { PageHeroProps, HeroMetaCardProps };
